import _ from 'lodash';
import { clsx } from 'clsx';
import { Box, Checkbox } from '@material-ui/core';
import { CheckBoxOutlineBlank, CheckBoxOutlined } from '@material-ui/icons';

import { ProductComment } from './components/product-comment';
import { SuggestedProductComment } from './components/suggested-product-comment';
import Typography from '../common/typography';
import CustomCheckbox from '../common/custom-checkbox/CustomCheckbox';
import { TrashIcon } from '../icons/trash-icon';
import {
  ChipOrderDisclaimer,
  OrderMgmtDisclaimer,
} from '../order-management/components/order-mgmt-dialog/components';
import {
  SFStockoutItemChip,
  SubstitutionFlowDeleteItemBtn,
  SubstitutionFlowToppingHOC,
} from 'features/order/substitution-flow';

import { useFormatMessage } from '../../intl/intl';
import { formatCurrency } from '../../utils/CurrencyUtils';
import { useOrderManagement } from '../order-management/hooks';

import StringUtils from '../../utils/StringUtils';
import { orderDetailCardStyles } from './OrderDetailCard.styles';
import { FORMAT_SPACES } from './const';

import type { OrderId } from 'shared/domain';

export function OrderDetailCard(props: Props) {
  const {
    classes,
    orderId,
    product,
    showAddWhimsButton,
    deactivateProductIds,
    deactivateToppingIds,
    handleCheckDefectProduct,
    isDefectProductsChecked,
    // a ReactNode passed as a prop by SubstitutionFlowProductHOC
    showStockoutProductChip,
    isReplacementRemovable,
    isBeingManaged,
  } = props;

  const {
    isSendingUserApproval,
    hasNoUserResponse,
    handleCheckProductSelected,
    handleSelectTopping,
    checkIfProductIsSelected,
    checkIfToppingIsSelected,
    checkIfProductHasToppingsSelected,
    hasOrderOnlyOneProduct,
    handleRemoveProduct,
    handleCheckIfProductHasBeenRemoved,
    hasOrderProductsRemoved,
    hasOrderProductsSelected,
  } = useOrderManagement();

  const {
    comments,
    description,
    orderedToppings,
    id,
    name,
    quantityText,
    toppingMessage,
    total_real_price,
    replacement_suggestion,
    has_defect_rate_report,
    global_offers,
    isDefectChecked,
    order_product_id,
  } = product;

  // TODO: need full refactor, using useOrderDetailCard hook
  const isProductDeactivated = deactivateProductIds.includes(id);
  const isProductSelected = checkIfProductIsSelected(id);
  const _classes = orderDetailCardStyles();
  const globalOfferTagPromo =
    global_offers?.[0]?.conditions?.[0]?.tag_promo ?? '';
  const defectRateTitle = useFormatMessage({ id: 'defectRate.title' });
  const defectRateCaption = useFormatMessage({ id: 'defectRate.caption' });
  const commentsText = useFormatMessage({
    id: 'orderStatusCookingDetail.comments',
  });
  const addValue = useFormatMessage({
    id: 'orderStatusCookingDetail.addValue',
  });

  const globalOfferText = useFormatMessage(
    { id: 'globalOffer.text' },
    {
      promo: globalOfferTagPromo,
    },
  );
  const globalOfferMsgError = useFormatMessage({ id: 'globalOffer.msg.error' });

  const globalOfferPromotion = useFormatMessage({
    id: 'globalOffer.promotion',
  });

  const disclaimerDescriptionToProduct = useFormatMessage({
    id: 'orderManagement.deactivateItems.disclaimerDescription.toProduct',
  });
  const disclaimerDescriptionToTopping = useFormatMessage({
    id: 'orderManagement.deactivateItems.disclaimerDescription.toTopping',
  });
  const deactivateItemsDescriptionText = ({ item, itemName }) => {
    const _text =
      item === 'product'
        ? disclaimerDescriptionToProduct
        : disclaimerDescriptionToTopping;
    return _text.replace('_itemName_', itemName);
  };
  const deactivateItemsTitleText = useFormatMessage({
    id: 'orderManagement.deactivateItems.disclaimerTitle',
  });
  const soldOutProductText = useFormatMessage({
    id: 'orderManagement.deactivateItems.soldOutProduct',
  });
  const soldOutToppingText = useFormatMessage({
    id: 'orderManagement.deactivateItems.soldOutTopping',
  });

  const isReplacementSuggestionValid = !StringUtils.isNullOrEmpty(
    replacement_suggestion,
  );
  const isNullGlobalOffer =
    global_offers === null ||
    global_offers?.[0]?.conditions === null ||
    global_offers?.[0]?.conditions?.[0]?.tag_promo === null;
  const hasGlobalOffer = !StringUtils.isNullOrEmpty(globalOfferTagPromo);
  const showDetail = !_.isEmpty(orderedToppings) || comments || description;
  const _HIDE_OM = true;
  const showDeactivatedProduct = isProductDeactivated && !_HIDE_OM;
  const showDeleteItemButton = isReplacementRemovable && isBeingManaged;

  interface ToppingDetailProps {
    topping: any;
    showStockoutToppingChip?: boolean;
  }
  const ToppingDetail = (_props: ToppingDetailProps) => {
    const { topping, showStockoutToppingChip = false } = _props;
    return (
      <div
        aria-label='toppingDetail'
        className={_classes.toppingDetail}
        data-is-stockout={showStockoutToppingChip}
      >
        <Box
          display='flex'
          width='100%'
          alignItems='center'
          justifyContent='space-between'
        >
          <Box display='flex' alignItems='center' gridGap='0.5rem'>
            <p
              aria-label='toppingDescription'
              className={_classes.toppingDescription}
            >
              {'\xa0'.repeat(topping.level * FORMAT_SPACES.description)}
              <span>{`${topping.units}x ${topping.description}`}</span>
            </p>
            {showStockoutToppingChip && <SFStockoutItemChip mode='stockout' />}
            {/*{isToppingDeactivated && !_HIDE_OM && (
              <ChipOrderDisclaimer
                type='warning'
                disclaimer={soldOutToppingText}
                classes={{
                  disclaimer: _classes.chipDisclaimer,
                  text: _classes.chipText,
                }}
              />
            )}*/}
          </Box>
          <Box className={_classes.toppingOptions}>
            {(isSendingUserApproval || hasNoUserResponse) && (
              <CustomCheckbox
                classes={{
                  root: _classes.checkTopping,
                }}
                checked={checkIfToppingIsSelected(id, topping.id)}
                onChange={() => handleSelectTopping(id, topping.id)}
                value={checkIfToppingIsSelected(id, topping.id)}
                disabled={
                  isProductSelected ||
                  checkIfProductHasToppingsSelected(id, topping.id)
                }
              />
            )}
          </Box>
          <p
            aria-label='toppingPrice'
            data-is-stockout={showStockoutToppingChip}
            className={_classes.toppingPrice}
          >
            {formatCurrency(topping.total_real_price)}
          </p>
        </Box>
        {/*{isToppingDeactivated && !_HIDE_OM && (
          <OrderMgmtDisclaimer
            description={deactivateItemsDescriptionText({
              item: 'topping',
              itemName: topping.description,
            })}
          />
        )}*/}
      </div>
    );
  };

  return (
    !handleCheckIfProductHasBeenRemoved(id) && (
      <div
        data-is-stockout={showStockoutProductChip}
        className={clsx(_classes.root, classes?.root)}
      >
        <div
          aria-label='product-detail-wrapper'
          data-is-stockout={showStockoutProductChip}
          className={clsx(_classes.wrapper, classes?.wrapper)}
        >
          {isReplacementSuggestionValid && (
            <SuggestedProductComment
              productNameToBeReplaced={name}
              suggestedProduct={replacement_suggestion}
            />
          )}
          <div className={_classes.container}>
            {/* Header */}
            <Box className={_classes.detailHeader}>
              <span aria-label='quantity' className={_classes.quantity}>
                {quantityText}
              </span>
              <Box className={_classes.titleWrapper}>
                <Box display='flex' alignItems='center' gridGap='0.5rem'>
                  <p
                    aria-label='productTitle'
                    className={_classes.productTitle}
                  >
                    {name}
                  </p>
                  {showStockoutProductChip && (
                    <SFStockoutItemChip mode='stockout' />
                  )}
                  {showDeactivatedProduct && (
                    <ChipOrderDisclaimer
                      type='alert'
                      disclaimer={soldOutProductText}
                      classes={{
                        disclaimer: _classes.chipDisclaimer,
                        text: _classes.chipText,
                      }}
                    />
                  )}

                  {has_defect_rate_report && (
                    <ChipOrderDisclaimer
                      type='danger'
                      disclaimer={defectRateTitle}
                      classes={{
                        disclaimer: _classes.chipDisclaimer,
                        text: _classes.chipText,
                        icon: _classes.chipDangerIcon,
                      }}
                      hasIcon
                    />
                  )}
                  {(hasGlobalOffer || isNullGlobalOffer) && (
                    <ChipOrderDisclaimer
                      type='info'
                      disclaimer={globalOfferPromotion}
                      classes={{
                        disclaimer: _classes.chipDisclaimer,
                        text: _classes.chipText,
                      }}
                    />
                  )}
                </Box>
                {/* subtotal */}
                <div
                  aria-label='detailSubtotal'
                  className={_classes.detailSubtotal}
                >
                  <Typography
                    classes={{
                      root: clsx(
                        _classes.subtotalVal,
                        showStockoutProductChip && _classes.strikethrough,
                      ),
                    }}
                  >
                    {formatCurrency(total_real_price)}
                  </Typography>
                  {(isSendingUserApproval || hasNoUserResponse) && (
                    <Box display='flex' alignItems='center'>
                      <CustomCheckbox
                        classes={{ root: _classes.checkProduct }}
                        checked={isProductSelected}
                        onChange={() => handleCheckProductSelected(id)}
                        value={isProductSelected}
                        disabled={hasOrderProductsRemoved}
                      />
                      {isSendingUserApproval && (
                        <Box
                          className={clsx(_classes.deleteProduct, {
                            [_classes.deleteProductsDisabled]:
                              isProductSelected ||
                              hasOrderOnlyOneProduct ||
                              hasOrderProductsSelected,
                          })}
                          onClick={() =>
                            isProductSelected ||
                            hasOrderOnlyOneProduct ||
                            hasOrderProductsSelected
                              ? null
                              : handleRemoveProduct(id)
                          }
                        >
                          <TrashIcon
                            type={
                              isProductSelected ||
                              hasOrderOnlyOneProduct ||
                              hasOrderProductsSelected
                                ? 'disabled'
                                : 'default'
                            }
                          />
                        </Box>
                      )}
                    </Box>
                  )}
                </div>
              </Box>
            </Box>
            <Box>
              {showDeactivatedProduct && (
                <OrderMgmtDisclaimer
                  description={deactivateItemsDescriptionText({
                    item: 'product',
                    itemName: name,
                  })}
                />
              )}
              {has_defect_rate_report && (
                <Box
                  className={clsx(
                    _classes.defectRate,
                    _classes?.defectRateBorder,
                  )}
                >
                  <Typography classes={{ root: _classes.defectRateCaption }}>
                    {defectRateCaption}
                  </Typography>
                  <Checkbox
                    disabled={isDefectProductsChecked}
                    checked={isDefectChecked}
                    onChange={() => handleCheckDefectProduct(order_product_id)}
                    value={isDefectChecked}
                    icon={<CheckBoxOutlineBlank />}
                    checkedIcon={<CheckBoxOutlined />}
                    classes={{
                      root: _classes.defectCheckedRoot,
                      checked: _classes.defectChecked,
                    }}
                  />
                </Box>
              )}
              {hasGlobalOffer && (
                <Box
                  className={clsx(
                    _classes.globalOffer,
                    _classes?.globalOfferBorder,
                  )}
                >
                  <Box
                    className={_classes.globalOfferCaption}
                    dangerouslySetInnerHTML={{
                      __html: globalOfferText || '',
                    }}
                  />
                </Box>
              )}
              {isNullGlobalOffer && (
                <div
                  className={clsx(
                    _classes.globalOffer,
                    _classes?.globalOfferBorder,
                  )}
                >
                  <span className={_classes.globalOfferCaption}>
                    {globalOfferMsgError}
                  </span>
                </div>
              )}
            </Box>
            {/* order body */}
            {showDetail && (
              <div className={_classes.detailContainer}>
                <p
                  className={_classes.description}
                  hidden={description.trim().length <= 3}
                >
                  {description.trim()}
                </p>
                {/* Topping custom alert */}
                {toppingMessage && (
                  <div className={_classes.toppingDisclaimer}>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: toppingMessage,
                      }}
                    ></span>
                  </div>
                )}
                {orderedToppings.length >= 1 && (
                  <div className={_classes.toppings}>
                    {orderedToppings.map((topping: any, index: number) => {
                      const isToppingDeactivated = deactivateToppingIds.includes(
                        topping.id,
                      );
                      return (
                        <div
                          key={topping.order_product_topping_id}
                          data-level={topping.level}
                          data-topping-id={topping.id}
                          data-parent-id={String(topping.parentID)}
                          data-node-id={String(topping.toppingNodeId)}
                          data-category={topping.topping_category}
                        >
                          {topping.showCategory && (
                            <p className={_classes.toppingCategory}>
                              {'\xa0'.repeat(
                                topping.level * FORMAT_SPACES.category,
                              )}
                              {topping.topping_category}
                            </p>
                          )}
                          <SubstitutionFlowToppingHOC
                            orderId={orderId}
                            storeId={product.store_id}
                            customizationId={topping.id}
                            orderProductId={product.order_product_id}
                            isBeingManaged={isBeingManaged}
                          >
                            <ToppingDetail topping={topping} />
                          </SubstitutionFlowToppingHOC>
                        </div>
                      );
                    })}
                  </div>
                )}
                {!isReplacementSuggestionValid && comments && (
                  <ProductComment
                    textLabel={commentsText}
                    comments={comments}
                    showButton={showAddWhimsButton}
                    buttonLabel={addValue}
                    productName={name}
                    productId={id}
                  />
                )}
              </div>
            )}
            {showDeleteItemButton && (
              <div className={_classes.deleteItemButtonContainer}>
                <SubstitutionFlowDeleteItemBtn
                  orderId={orderId}
                  storeId={product.store_id}
                  orderProductId={product.order_product_id}
                  itemId={product.id}
                  isProduct
                />
              </div>
            )}
          </div>
        </div>
      </div>
    )
  );
}

interface Props {
  classes: any;
  intl: { formatMessage: any };
  orderId: OrderId;
  product: any;
  showAddWhimsButton: boolean;
  deactivateProductIds: Array<number>;
  deactivateToppingIds: Array<number>;
  handleCheckDefectProduct: (id: number) => {};
  isDefectProductsChecked: boolean;
  showStockoutProductChip: boolean;
  isReplacementRemovable: boolean;
  isBeingManaged: boolean;
}
